/**
 * DataGrid — thin wrapper around AgGridReact that applies FAIM2 theme + defaults.
 *
 * This component owns ZERO feature/domain logic. Pages pass their own:
 *  - rowData
 *  - columnDefs
 *  - event handlers (onRowClicked, etc.)
 *
 * All AgGridReact props are forwarded so pages have full control.
 */
import { useCallback, useState } from 'react';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import type { ColDef, FirstDataRenderedEvent, ModelUpdatedEvent } from 'ag-grid-community';
import { faim2Theme, faim2ThemeDark } from './theme';
import { defaultColDef as sharedDefaultColDef } from './defaults';
import { useTheme } from '../../theme';

export type DataGridProps<TData = unknown> = AgGridReactProps<TData> & {
  /** CSS height for the grid container. Defaults to 600px. */
  height?: string | number;
};

export function DataGrid<TData = unknown>({
  height = 600,
  defaultColDef,
  theme,
  onFirstDataRendered,
  onModelUpdated,
  ...rest
}: DataGridProps<TData>) {
  const { theme: currentTheme } = useTheme();
  const gridTheme = theme ?? (currentTheme === 'theme-dark' ? faim2ThemeDark : faim2Theme);
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  const totalRowCount = rest.rowData?.length ?? 0;

  const updateVisibleRowCount = useCallback((event: FirstDataRenderedEvent<TData> | ModelUpdatedEvent<TData>) => {
    setVisibleRowCount(event.api.getDisplayedRowCount());
  }, []);

  return (
    <div className="flex flex-col" style={{ height }}>
      <div className="min-h-0 flex-1">
        <AgGridReact<TData>
          theme={gridTheme}
          defaultColDef={{ ...sharedDefaultColDef, ...defaultColDef } as ColDef<TData>}
          onFirstDataRendered={(event) => {
            updateVisibleRowCount(event);
            onFirstDataRendered?.(event);
          }}
          onModelUpdated={(event) => {
            updateVisibleRowCount(event);
            onModelUpdated?.(event);
          }}
          {...rest}
        />
      </div>
      {!rest.pagination && (
        <footer
          className="flex h-9 items-center border border-t-0 border-border bg-container-bg px-3 text-sm text-foreground-faded"
          aria-live="polite"
        >
          Rows: {visibleRowCount} of {totalRowCount}
        </footer>
      )}
    </div>
  );
}
